<template>
    <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12">
            <div class="grid grid-cols-12 gap-4">
                <div class="col-span-10 mx-auto">
                    <form>
                        <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent form-text my-2" v-model="form.name"
                                      placeholder="Name of Role" />
                        <textarea v-model="form.description" placeholder="Description" rows="4" class="w-full px-3 py-2 text-sm border border-gray-300 rounded resize-vertical focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"></textarea>
                    </form>
                </div>
            </div>

            <div class="grid grid-cols-12 gap-4 mt-3">
                <div class="col-span-12">

                    <div class="grid grid-cols-12 gap-4" v-for="(group, idx) in groups" :key="idx">
                        <div class="col-span-12 border-t border-gray py-2">
                            <div class="grid grid-cols-12 gap-4">
                                <div class="col-span-3">
                                    <p>
                                        {{ cleanUnderscoreString(group.name.replace('-', ' ')) }}
                                    </p>
<!--                                    <button class="btn-primary bg-primary px-2 text-white rounded-sm"><i class="fa fa-check mr-1"/>select
                                        all
                                    </button>-->
                                </div>
                                <div class="col-span-9 border-l border-gray pl-4">
                                  <div class="flex flex-wrap gap-x-6 gap-y-3">
                                    <template v-for="(permission, pidx) in permissions" :key="pidx">
                                      <div v-if="group.name === permission.group" class="text-lowercase flex items-center">
                                        <label class="flex items-center gap-2 text-sm cursor-pointer select-none">
                                          <input type="checkbox" v-model="form.permissions" :value="permission.id" :name="cleanUnderscoreString(permission.name)" class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary focus:ring-2" />
                                          <span>{{ cleanUnderscoreString(permission.name) }}</span>
                                        </label>
                                      </div>
                                    </template>
                                  </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="grid grid-cols-12 gap-4">
                <div class="col-span-6 mx-auto">
                    <button class="btn-primary w-full bg-primary text-white px-3 py-3 my-5 rounded-sm hover:bg-primary-dark flex items-center justify-center gap-2"
                            :disabled="spinner"
                            :class="spinner ? 'opacity-70 cursor-not-allowed' : ''"
                            @click="submitRole">
                        <i :class="spinner ? 'fa fa-spinner fa-spin' : 'fa fa-save'"></i>
                        <span>{{ spinner ? 'Saving...' : (role_uid ? 'Save changes role' : 'Save Role') }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useToast } from '@/composables'

interface Permission {
    id: string
    name: string
    group: string
    permissionId?: string
}

interface Group {
    id: string
    name: string
}

interface RoleForm {
    name: string | null
    description: string | null
    permissions: string[]
    active_permissions?: string[]
}

interface Props {
    roleId?: string | null
}

const props = withDefaults(defineProps<Props>(), {
    roleId: null
})

const emit = defineEmits<{
    finished: [value: boolean]
}>()

const store = useStore()
const { successToast, errorToast } = useToast()

const errorMessage = ref<string | null>(null)
const checkPermissions = ref(false)
const permissions = ref<Permission[]>([])
const form = ref<RoleForm>({
    name: null,
    description: null,
    permissions: []
})
const groups = ref<Group[]>([])
const spinner = ref(false)
const role_uid = ref<string | null>(null)
const allPermissions = ref<string[]>([])
const permissionsToRemove = ref<string[]>([])
const loading = ref(false)

onMounted(() => {
    spinner.value = false
    store.dispatch("fetchPermissionGroups")
    store.dispatch("fetchPermissions")
})

const cleanUnderscoreString = (str: string) => {
    if (!str) return ''
    return str.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

const submitRole = () => {
    spinner.value = true
    errorMessage.value = null
    if (role_uid.value) {
        permissionsToRemove.value = []
        permissions.value.forEach(permission => {
            if (!form.value.permissions.includes(permission.id)) {
                permissionsToRemove.value.push(permission.id)
            }
        })
        let data = {
            description: form.value.description,
            name: form.value.name,
            permissions: allPermissions.value,
            permissionsToAdd: form.value.permissions,
            permissionsToRemove: permissionsToRemove.value,
            roleId: role_uid.value
        }
        store.dispatch("updateRoleAndPermission", data)
    } else {
        store.dispatch("saveNewRole", form.value)
    }
}

const filterRoleEdit = (roleDataEdit: any) => {
    let role_data = roleDataEdit
    form.value.name = role_data.name
    form.value.description = role_data.description
    role_uid.value = role_data.uid
    let role = role_data.rolePermissions
    form.value.active_permissions = []
    for (let i = 0; i < role.length; i++) {
        if (role[i].value === "Yes") {
            form.value.active_permissions.push(role[i].permission)
        }
    }

    store.dispatch("fetchRoles")
}

// Watchers
watch(() => store.getters.getPermissionGroupsList, (data) => {
    loading.value = false
    groups.value = data
})

watch(() => store.getters.getRolesError, (rolesError) => {
    if (rolesError) {
        spinner.value = false
        errorMessage.value = rolesError.message
        // @ts-ignore
        errorToast("Error", rolesError.message)
    }
})

watch(() => store.getters.getPermissionList, (permissionList) => {
    permissions.value = permissionList
})

watch(() => store.getters.getRolesAndPermissionUpdate, (roleDataUpdate) => {
    if (roleDataUpdate) {
        // @ts-ignore
        successToast("Success", "Role updated successfully")
        spinner.value = false
        form.value.permissions = []
        permissionsToRemove.value = []
        form.value.name = null
        form.value.description = null
        role_uid.value = null
        emit("finished", true)
        store.dispatch("fetchRoles")
        store.dispatch("fetchPermissionGroups")
        store.dispatch("fetchPermissions")
    }
})

watch(() => store.getters.getNewRole, (data) => {
    if (data) {
        // @ts-ignore
        successToast("Role added")
        spinner.value = false
        form.value = {
            name: null,
            description: null,
            permissions: []
        }
        emit("finished", true)
    }
})

watch(() => store.getters.getNewRoleError, (data) => {
    if (data) {
        spinner.value = false
        // @ts-ignore
        errorToast()
    }
})

watch(() => store.getters.getRoleDetails, (roleDetails) => {
    if (roleDetails) {
        form.value.name = roleDetails.name
        form.value.description = roleDetails.description
        role_uid.value = roleDetails.roleId

        form.value.permissions = []

        roleDetails.permissions.forEach((permission: Permission) => {
            if (permission.permissionId) {
                form.value.permissions.push(permission.permissionId)
            }
        })

        allPermissions.value = []
        permissionsToRemove.value = []
        permissions.value.forEach(permission => {
            allPermissions.value.push(permission.id)
        })
    }
})

defineExpose({
    filterRoleEdit
})
</script>
